<script lang="tsx" setup>
import Modal from '../shared/Modal.vue'
import { computed, nextTick, onBeforeMount, ref, watch, type WatchStopHandle } from 'vue'
import type {
	Response,
	MassChanges,
	MassEditRule,
	MassEdit,
	MassEditRuleInfo,
	Responsible,
	CustomFieldDefinition,
	URLReferenceChanges,
	DocumentReferenceChanges,
} from '@/models/api-generated'
import type { DocumentReference, URLReference } from '@/models/api-generated'

import { type MethodEditModel } from '@/stores/method-editor'
import http from '@/services/http'
import { RuleImplemented, RuleNotImplemented, RuleWillNotApply, RuleWillNotImplement, type RuleStatus } from '@/models/enums-generated'
import PrioritySelect from '@/components/widgets/PrioritySelect.vue'
import RuleStatusSelect from '@/components/widgets/RuleStatusSelect.vue'
import { promptForRequiredComment } from './required-comment'
import ResponsibleSelect from '../widgets/ResponsibleSelect.vue'
import { formatLocale, getDateFormat } from '@/services/utils'
import Datepicker from '@vuepic/vue-datepicker'
import CustomValueEdit from '../custom_field/CustomValueEdit.vue'
import URLReferenceMass from '../comment_references/URLReferenceMass.vue'
import DocumentReferenceMass from '../comment_references/DocumentReferenceMass.vue'
import { useTheme } from '../user/useTheme'

const { theme } = useTheme()
const emit = defineEmits(['massEdit'])
const model = defineModel<MethodEditModel>({ required: true })
const rules = ref<MassEditRuleInfo[]>()
const changes = ref<MassChanges>({
	responsible: { enabled: false, value: undefined },
	priority: { enabled: false, value: '' },
	price: { enabled: false, value: 0 },
	dueDate: { enabled: false, value: '' },
	comment: { enabled: false, value: '' },
	rules: {},
	metaCustomValues: {},
	serviceProvider: { enabled: false, value: undefined },
	urlReferences: { enabled: false, value: { newReferences: [], referencesToDelete: [], referencesToChange: [] } },
	documentReferences: { enabled: false, value: { newReferences: [], referencesToDelete: [], referencesToChange: [] } },
})
const isInitializing = ref(false)

const displayUrls = ref<URLReferenceChanges[]>([])
const displayDocuments = ref<DocumentReferenceChanges[]>([])

const metaCustom = ref<CustomFieldDefinition[]>([])

const emptyDocumentReference = (): DocumentReference => ({
	id: 0,
	title: '',
	ownerId: 0 as any,
	ownerType: '' as any,
	documentId: 0,
	document: undefined as any,
})

function onUrlRemoved(urlRef: URLReference) {
	changes.value.urlReferences.value?.referencesToDelete.push(urlRef)
	changes.value.urlReferences.enabled = true
}

function onDocRemoved(docRef: DocumentReference) {
	changes.value.documentReferences.value?.referencesToDelete.push(docRef)
	changes.value.documentReferences.enabled = true
}

async function getCustomFields() {
	let { data: meta } = await http.get<Response<CustomFieldDefinition[]>>(`api/customFields/standard_method_meta_data`)
	if (meta.data?.length) metaCustom.value = meta.data
}

for (const edit of Object.values(changes.value)) {
	const e = edit as MassEdit<any>
	if (e.enabled !== undefined) {
		watch(
			() => e.value,
			() => {
				if (isInitializing.value) return
				e.enabled = true
			},
			{ deep: true },
		)
	}
}

let defaults: {
	responsible?: Responsible
	priority?: string
	price?: number
	dueDate?: string
	comment?: string
	serviceProvider?: Responsible
	rules: {
		[ruleId: number]: {
			status?: RuleStatus
			comment?: string
		}
	}
}

const methodChecked = ref(false)

const fieldEdited = computed(() => {
	const methodsActive =
		Object.values(changes.value).some((field: any) => field?.enabled) ||
		Object.values(changes.value.metaCustomValues).some((cf) => cf.enabled)

	if (model.value.method === null) return methodsActive || model.value.override

	const rulesActive = Object.values(changes.value.rules).some((rule) => rule.status.enabled || rule.comment.enabled)
	return methodsActive || rulesActive
})

const canSave = computed(() => {
	for (const a of Object.values(changes.value.rules)) {
		if (a.status.enabled && !a.isSubrule && (a.status.value === RuleWillNotImplement || a.status.value === RuleWillNotApply)) {
			if (!a.comment.value) return false
		}
	}

	return methodChecked.value || fieldEdited.value
})

function setDefault(key: string) {
	let value: any | undefined
	let valueExists = false

	for (const meta of model.value.selected) {
		const am = meta as any
		if (!am[key]) return
		if (!valueExists) {
			value = am[key]
			valueExists = true
		} else if (am[key]?.id !== undefined) {
			if (value.id !== am[key].id) return
		} else {
			if (am[key] !== value) return
		}
	}

	;(defaults as any)[key] = value
}

function setDefaultRules(r: MassEditRuleInfo) {
	let status: RuleStatus | undefined
	let comment: string | undefined

	for (const meta of r.metas) {
		if (status === undefined) status = meta.status
		if (status !== meta.status) break
	}

	for (const meta of r.metas) {
		if (comment === undefined) comment = meta.comment
		if (comment !== meta.comment) break
	}

	if (status === undefined) status = RuleNotImplemented
	if (comment === undefined) comment = ''

	defaults.rules[r.id] = {
		status,
		comment,
	}

	r.subRules.forEach(setDefaultRules)
}

function findDeep(ruleId: number): MassEditRuleInfo | undefined {
	if (!rules.value) return

	for (const x of rules.value) {
		if (x.id === ruleId) return x

		for (const y of x.subRules) {
			if (y.id === ruleId) return y
		}
	}

	return undefined
}

const ruleWatchers: WatchStopHandle[] = []

function constructRuleChanges(ruleId: number, isSubrule: boolean) {
	changes.value.rules[ruleId] = {
		status: { enabled: false, value: defaults.rules[ruleId].status },
		comment: { enabled: false, value: defaults.rules[ruleId].comment },
		isSubrule: isSubrule,
	}

	ruleWatchers.push(
		watch(
			() => changes.value.rules[ruleId].status.value,
			(status: RuleStatus) => {
				changes.value.rules[ruleId].status.enabled = true

				const rule = findDeep(ruleId)

				// Changing parent rule status not implemented
				/*if (rule?.parentId) {
					changes.value.rules[rule.parentId].status.value = status
				} else */ if (rule?.subRules.length) {
					rule.subRules.forEach((x) => {
						changes.value.rules[x.id].status.value = status
					})
				}
			},
		),
		watch(
			() => changes.value.rules[ruleId].comment.value,
			() => (changes.value.rules[ruleId].comment.enabled = true),
		),
	)
}

function constructCustomChanges(definitions: CustomFieldDefinition[], values: { [key: number]: MassEdit<string> }) {
	for (const cf of definitions) {
		values[cf.id] = {
			enabled: false,
		}

		ruleWatchers.push(
			watch(
				() => values[cf.id].value,
				() => (values[cf.id].enabled = true),
			),
		)
	}
}

watch(
	() => model.value.visible,
	async () => {
		isInitializing.value = true

		if (ruleWatchers.length) {
			ruleWatchers.forEach((x) => x())
			ruleWatchers.splice(0, ruleWatchers.length)
		}

		methodChecked.value = false

		defaults = { rules: {} }
		Object.keys(changes.value)
			.filter((x) => x !== 'rules' && x !== 'methodCustomValues' && x !== 'metaCustomValues')
			.forEach(setDefault)

		changes.value.metaCustomValues = {}
		constructCustomChanges(metaCustom.value, changes.value.metaCustomValues)

		if (model.value.method) {
			const { data } = await http.post<Response<MassEditRuleInfo[]>>(
				'/api/massEditMethodMetaData/rules',
				model.value.selected.map((x) => x.id),
			)
			if (data.data) rules.value = data.data

			for (const r of rules.value ?? []) setDefaultRules(r)

			changes.value.rules = {}
			for (const a of rules.value ?? []) {
				constructRuleChanges(a.id, false)

				for (const b of a.subRules) {
					constructRuleChanges(b.id, true)
				}
			}
		}

		displayUrls.value = commonUrlReferenceChanges()
		displayDocuments.value = commonDocumentReferenceChanges()
		applyDefaults()

		nextTick(() => {
			isInitializing.value = false
		})
	},
)

function inputClass(a: MassEdit<any>) {
	return a.enabled ? 'enabled' : ''
}

function commentClass(rule: MassEditRule) {
	const isInvalid =
		(rule.status.value === RuleWillNotImplement || rule.status.value === RuleWillNotApply) && !rule.comment.value && !rule.isSubrule
	return {
		enabled: rule.comment.enabled && !isInvalid,
		'is-invalid': isInvalid,
	}
}

function applyDefaults() {
	delete model.value.override

	methodChecked.value = false

	changes.value.responsible.value = defaults.responsible
	changes.value.priority.value = defaults.priority
	changes.value.price.value = defaults.price
	changes.value.dueDate.value = defaults.dueDate
	changes.value.comment.value = defaults.comment
	changes.value.serviceProvider.value = defaults.serviceProvider
	for (const [ruleId, d] of Object.entries(defaults.rules)) {
		changes.value.rules[Number(ruleId)].status.value = d.status
		changes.value.rules[Number(ruleId)].comment.value = d.comment
	}
	nextTick(() => {
		changes.value.responsible.enabled = false
		changes.value.priority.enabled = false
		changes.value.price.enabled = false
		changes.value.dueDate.enabled = false
		changes.value.comment.enabled = false
		changes.value.serviceProvider.enabled = false
		for (const ruleId of Object.keys(defaults.rules)) {
			changes.value.rules[Number(ruleId)].status.enabled = false
			changes.value.rules[Number(ruleId)].comment.enabled = false
		}
		changes.value.urlReferences.enabled = false
		changes.value.documentReferences.enabled = false
	})
}

function setRuleStatus(status: RuleStatus) {
	let cmt: string | null | undefined
	if (status == RuleWillNotImplement || status == RuleWillNotApply) {
		cmt = promptForRequiredComment()
		if (cmt) changes.value.comment.value = cmt
	}

	for (const a of Object.values(changes.value.rules)) {
		a.status.enabled = true
		a.status.value = status

		if (cmt) {
			a.comment.enabled = true
			a.comment.value = cmt
		}
	}

	const btn = document.querySelector('button[data-bs-target="#masseditmethod-rules"]') as HTMLButtonElement
	btn.click()
}

function overrideRuleStatus(status: RuleStatus) {
	let cmt: string | null | undefined
	if (!model.value.override?.comment && (status == RuleWillNotImplement || status == RuleWillNotApply)) {
		cmt = promptForRequiredComment()
		if (cmt === null) return
		if (cmt) changes.value.comment.value = cmt
	}

	model.value.override = {
		status,
		comment: cmt ?? changes.value.comment.value,
	}
}

function apply() {
	model.value.visible = false
	emit('massEdit', changes.value)
}

onBeforeMount(async () => {
	await getCustomFields()
})

function normalizeUrl(url: string) {
	return (url || '').trim().toLowerCase()
}

function commonUrlReferences(): URLReference[] {
	if (!model.value.selected.length) return []
	const counts = new Map<string, { ref: URLReference; seen: number }>()
	// Initialize with first meta's set
	const first = model.value.selected[0].urlReferences || []
	for (const r of first) {
		counts.set(normalizeUrl(r.url.url), { ref: { ...r }, seen: 1 })
	}
	for (let i = 1; i < model.value.selected.length; i++) {
		const current = model.value.selected[i].urlReferences || []
		const currentSet = new Set(current.map((r) => normalizeUrl(r.url.url)))
		for (const key of counts.keys()) {
			if (currentSet.has(key)) {
				const entry = counts.get(key)!
				entry.seen++
			}
		}
	}
	const all = model.value.selected.length
	return Array.from(counts.values())
		.filter((x) => x.seen === all)
		.map((x) => {
			const { ...rest } = x.ref as any
			return rest
		})
}
const emptyUrlReference = (): URLReference => ({
	id: 0,
	urlId: 0,
	title: '',
	url: { id: 0, url: '', path: '/', referenceCount: 0, author: sessionMap.user, instance: undefined as any },
	ownerId: 0 as any,
	ownerType: '' as any,
})

function commonUrlReferenceChanges(): URLReferenceChanges[] {
	return commonUrlReferences().map((ref) => ({
		oldReferences: { ...ref },
		changedReferences: { ...ref },
	}))
}

watch(
	() => displayUrls.value,
	(urls) => {
		const payload = changes.value.urlReferences.value
		if (isInitializing.value || !payload) return

		const newRefs: URLReference[] = []
		const changedRefs: URLReferenceChanges[] = []

		for (const entry of urls) {
			const oldRef = entry.oldReferences ?? emptyUrlReference()
			const newRef = entry.changedReferences ?? emptyUrlReference()
			const hasOriginalUrl = !!normalizeUrl(oldRef.url.url)
			const hasOriginalId = !!oldRef.id
			const isNew = !hasOriginalUrl && !hasOriginalId
			const changed = oldRef.title !== newRef.title || normalizeUrl(oldRef.url.url) !== normalizeUrl(newRef.url.url)

			if (isNew) {
				if (newRef.url?.url.trim() || newRef.title?.trim()) {
					newRefs.push({ ...newRef, id: 0, ownerId: 0 as any, ownerType: '' as any })
				}
			} else if (changed) {
				changedRefs.push({
					oldReferences: { ...oldRef },
					changedReferences: { ...newRef, id: 0, ownerId: 0 as any, ownerType: '' as any },
				})
			}
		}

		payload.newReferences = newRefs
		payload.referencesToChange = changedRefs
		changes.value.urlReferences.enabled = Boolean(newRefs.length || changedRefs.length || payload.referencesToDelete.length)
	},
	{ deep: true },
)
watch(
	() => displayDocuments.value,
	(docs) => {
		const payload = changes.value.documentReferences.value
		if (isInitializing.value || !payload) return

		const newRefs: DocumentReference[] = []
		const changedRefs: DocumentReferenceChanges[] = []

		for (const entry of docs) {
			const oldRef = entry.oldReferences ?? emptyDocumentReference()
			const newRef = entry.changedReferences ?? emptyDocumentReference()
			const hasOriginalDoc = !!oldRef.documentId
			const isNew = !hasOriginalDoc || !oldRef.id
			const changed = oldRef.title !== newRef.title || (oldRef.documentId ?? 0) !== (newRef.documentId ?? 0)

			if (isNew) {
				if ((newRef.documentId ?? 0) > 0 || newRef.title?.trim()) {
					newRefs.push({ ...newRef, id: 0, ownerId: 0 as any, ownerType: '' as any })
				}
			} else if (changed) {
				changedRefs.push({
					oldReferences: { ...oldRef },
					changedReferences: { ...newRef, id: 0, ownerId: 0 as any, ownerType: '' as any },
				})
			}
		}

		payload.newReferences = newRefs
		payload.referencesToChange = changedRefs
		changes.value.documentReferences.enabled = Boolean(newRefs.length || changedRefs.length || payload.referencesToDelete.length)
	},
	{ deep: true },
)

watch(
	() => model.value.visible,
	(visible) => {
		if (visible) return
		changes.value.urlReferences.value = {
			...changes.value.urlReferences.value!,
			referencesToDelete: [],
		}
		changes.value.documentReferences.value = {
			...changes.value.documentReferences.value!,
			referencesToDelete: [],
		}
	},
)

function commonDocumentReferenceChanges(): DocumentReferenceChanges[] {
	return commonDocumentReferences().map((ref) => ({
		oldReferences: { ...ref },
		changedReferences: { ...ref },
	}))
}

function commonDocumentReferences(): DocumentReference[] {
	if (!model.value.selected.length) return []

	const first = model.value.selected[0].documentReferences || []
	if (!first.length) return []

	const isSameRef = (a: DocumentReference, b: DocumentReference): boolean => {
		const aId = (a as any).documentId ?? 0
		const bId = (b as any).documentId ?? 0
		if (!aId || !bId) return false
		return aId === bId && (a.title ?? '') === (b.title ?? '')
	}

	let commonIndices = new Set<number>(first.map((_, idx) => idx))

	for (let i = 1; i < model.value.selected.length && commonIndices.size; i++) {
		const current = model.value.selected[i].documentReferences || []
		const next = new Set<number>()

		for (const idx of commonIndices) {
			const base = first[idx]
			const match = current.find((r) => isSameRef(base, r))
			if (match) next.add(idx)
		}

		commonIndices = next
	}

	return Array.from(commonIndices)
		.sort((a, b) => a - b)
		.map((idx) => {
			const r = first[idx]
			const { ...rest } = r as any
			return rest
		})
}
</script>

<template>
	<Modal class="modal-lg" data-test="masseditmethod-modal" v-model="model.visible">
		<template #header>
			<div class="row">
				<div class="ellipsis col-11">
					<template v-if="model.method">
						{{ model.method.name }}
						-
						{{ model.method.description }}
					</template>
					<template v-else>
						{{ $t('massEdit.label') }}
					</template>
					<div class="col-1">({{ model.selected.length }})</div>
				</div>
			</div>
		</template>
		<template #body>
			<template v-if="model && model.visible && model.method !== undefined">
				<ul class="nav nav-tabs justify-content-between" role="tablist">
					<li class="nav-item col-5" role="presentation">
						<button
							class="nav-link w-100 active"
							data-bs-toggle="tab"
							data-bs-target="#masseditmethod-method"
							type="button"
							role="tab"
							aria-controls="home"
							aria-selected="true"
						>
							{{ $t('methods') }}
						</button>
					</li>
					<li class="col-2"></li>
					<li class="nav-item col-5" role="presentation">
						<button
							class="nav-link w-100"
							data-bs-toggle="tab"
							data-bs-target="#masseditmethod-rules"
							type="button"
							role="tab"
							aria-controls="profile"
							aria-selected="false"
							:disabled="model.method === null"
						>
							{{ $t('rule.label', 2) }}
						</button>
					</li>
				</ul>

				<!-- Tab panes -->
				<div class="tab-content mt-4">
					<div class="tab-pane active" id="masseditmethod-method" role="tabpanel">
						<div class="container">
							<div class="row mb-3">
								<div class="col-12">
									<div class="alert alert-primary">
										{{ $t('massEdit.warning') }}
									</div>
								</div>
							</div>
							<div class="row mb-3">
								<label for="masseditmethod-responsible" class="col-3 col-form-label">{{ $t('responsible') }}</label>
								<div class="col">
									<ResponsibleSelect
										id="masseditmethod-responsible"
										:placeholder="$t('responsible') + '...'"
										v-model="changes.responsible.value"
										:class="inputClass(changes.responsible)"
									></ResponsibleSelect>
								</div>
							</div>
							<div class="row mb-3">
								<label for="masseditmethod-service-provider" class="col-3 col-form-label">{{ $t('method.serviceProvider') }}</label>
								<div class="col">
									<ResponsibleSelect
										id="masseditmethod-service-provider"
										:placeholder="$t('method.serviceProvider') + '...'"
										v-model="changes.serviceProvider.value"
										:class="inputClass(changes.serviceProvider)"
									></ResponsibleSelect>
								</div>
							</div>
							<div class="row mb-3">
								<label for="masseditmethod-priority" class="col-3 col-form-label">{{ $t('priority') }}</label>
								<div class="col">
									<PrioritySelect
										id="masseditmethod-priority"
										v-model="changes.priority.value"
										:class="inputClass(changes.priority)"
									></PrioritySelect>
								</div>
							</div>
							<div class="row mb-3">
								<label for="masseditmethod-price" class="col-3 col-form-label">{{ $t('price') }}</label>
								<div class="col">
									<input
										type="number"
										class="form-control"
										id="masseditmethod-price"
										v-model="changes.price.value"
										:class="inputClass(changes.price)"
									/>
								</div>
							</div>
							<div class="row mb-3">
								<label for="masseditmethod-duedate" class="col-3 col-form-label">{{ $t('date.dueDate') }}</label>
								<div class="col">
									<Datepicker
										v-model="changes.dueDate.value"
										id="masseditmethod-duedate"
										:clearable="true"
										:teleport="true"
										:enable-time-picker="false"
										:format="getDateFormat()"
										:format-locale="formatLocale()"
										:select-text="$t('misc.select')"
										:cancel-text="$t('misc.cancel')"
										text-input
										class="min-input-size"
										:class="inputClass(changes.dueDate)"
										:dark="theme === 'dark'"
									/>
								</div>
							</div>
							<div class="row mb-3">
								<label for="masseditmethod-comment" class="col-3 col-form-label">{{ $t('misc.comment') }}</label>
								<div class="col">
									<textarea
										class="form-control"
										id="masseditmethod-comment"
										v-model="changes.comment.value"
										:class="inputClass(changes.comment)"
										maxlength="500"
									></textarea>
								</div>
							</div>

							<template v-for="field in metaCustom" :key="field.id">
								<div class="row mb-3">
									<label :for="'masseditmethod-custom' + field.id" class="col-3 col-form-label">{{ field.label }}</label>
									<div class="col">
										<CustomValueEdit
											:id="'masseditmethod-custom' + field.id"
											:class="inputClass(changes.metaCustomValues[field.id])"
											:field="field"
											v-model="changes.metaCustomValues[field.id].value"
										/>
									</div>
								</div>
							</template>

							<div class="container border p-4 rounded w-100 d-flex">
								<div class="flex-grow-1">
									<URLReferenceMass
										v-model="displayUrls"
										:owner-id="model.method?.id ?? 0"
										owner-type="standard_method_meta_data"
										:can-edit="true"
										:can-add="true"
										@removed="onUrlRemoved"
									/>
								</div>

								<div class="vr mx-3"></div>

								<div class="flex-grow-1">
									<DocumentReferenceMass
										v-model="displayDocuments"
										:owner-id="model.method?.id ?? 0"
										owner-type="standard_method_meta_data"
										:can-edit="true"
										:can-add="true"
										@removed="onDocRemoved"
									/>
								</div>
							</div>

							<div class="row mt-4">
								<div class="col-12 d-inline-flex justify-content-between gap-2">
									<template v-if="model.method === null">
										<button
											id="method-will-not-implement-btn"
											class="btn btn-outline-danger"
											:class="{ active: model.override?.status === RuleWillNotImplement }"
											@click="overrideRuleStatus(RuleWillNotImplement)"
										>
											{{ $t('methodWillNotImplement') }}
										</button>
										<button
											id="method-will-not-apply-btn"
											class="btn btn-outline-secondary"
											:class="{ active: model.override?.status === RuleWillNotApply }"
											@click="overrideRuleStatus(RuleWillNotApply)"
										>
											{{ $t('methodWillNotApply') }}
										</button>
										<button
											id="method-not-implemented-btn"
											class="btn btn-outline-warning"
											:class="{ active: model.override?.status === RuleNotImplemented }"
											@click="overrideRuleStatus(RuleNotImplemented)"
										>
											{{ $t('methodNotImplemented') }}
										</button>
										<button
											id="method-implement-btn"
											class="btn btn-outline-success"
											:class="{ active: model.override?.status === RuleImplemented }"
											@click="overrideRuleStatus(RuleImplemented)"
										>
											{{ $t('methodImplemented') }}
										</button>
									</template>
									<template v-else>
										<button id="method-will-not-implement-btn" class="btn btn-danger" @click="setRuleStatus(RuleWillNotImplement)">
											{{ $t('methodWillNotImplement') }}
										</button>
										<button id="method-will-not-apply-btn" class="btn btn-secondary" @click="setRuleStatus(RuleWillNotApply)">
											{{ $t('methodWillNotApply') }}
										</button>
										<button id="method-not-implemented-btn" class="btn btn-warning" @click="setRuleStatus(RuleNotImplemented)">
											{{ $t('methodNotImplemented') }}
										</button>
										<button id="method-implement-btn" class="btn btn-success" @click="setRuleStatus(RuleImplemented)">
											{{ $t('methodImplemented') }}
										</button>
									</template>
								</div>
							</div>
							<div v-if="model.override" class="alert alert-warning mt-3">
								{{ $t('massEdit.warningRules') }}
							</div>
							<div v-else class="row mt-3">
								<div class="col-12 d-flex justify-content-center">
									<button
										:class="['btn', methodChecked || fieldEdited ? 'btn-outline-primary' : 'btn-outline-secondary']"
										@click="methodChecked = !methodChecked"
									>
										<i v-if="methodChecked || fieldEdited" class="bi bi-check-circle"></i>
										{{ $t('review.methodLastReviewed') }}
									</button>
								</div>
							</div>
						</div>
					</div>
					<div class="tab-pane" id="masseditmethod-rules" role="tabpanel">
						<div class="container">
							<template v-for="(a, i) in rules" :key="a.id">
								<div class="row align-items-center mb-4">
									<div class="col-8">
										<div class="row mb-3">
											<div class="col-12">{{ a.text }}</div>
										</div>
										<div class="row">
											<label :for="'masseditmethod-comment' + i" class="col-3 col-form-label">{{ $t('misc.comment') }}</label>
											<div class="col">
												<textarea
													class="form-control"
													:id="'masseditmethod-comment' + i"
													:class="commentClass(changes.rules[a.id])"
													v-model="changes.rules[a.id].comment.value"
													maxlength="500"
												></textarea>
											</div>
										</div>
									</div>
									<div class="col-4">
										<RuleStatusSelect
											:class="inputClass(changes.rules[a.id].status)"
											v-model="changes.rules[a.id].status.value"
										></RuleStatusSelect>
									</div>
								</div>

								<div class="subrules" v-if="a.subRules.length">
									<template v-for="(b, j) in a.subRules" :key="b.id">
										<div class="row align-items-center mb-4">
											<div class="col-8">
												<div class="row mb-3">
													<div class="col-12">{{ b.text }}</div>
												</div>
												<div class="row">
													<label :for="'masseditmethod-comment' + j" class="col-3 col-form-label">{{ $t('misc.comment') }}</label>
													<div class="col">
														<textarea
															class="form-control"
															:id="'masseditmethod-comment' + j"
															:class="commentClass(changes.rules[b.id])"
															v-model="changes.rules[b.id].comment.value"
															maxlength="500"
														></textarea>
													</div>
												</div>
											</div>
											<div class="col-4">
												<RuleStatusSelect
													:class="inputClass(changes.rules[b.id].status)"
													v-model="changes.rules[b.id].status.value"
												></RuleStatusSelect>
											</div>
										</div>
										<hr v-if="j < a.subRules.length - 1" />
									</template>
								</div>

								<hr v-if="i < (rules?.length ?? 0) - 1" />
							</template>
						</div>
					</div>
				</div>
			</template>
		</template>
		<template #footer>
			<div class="w-100 d-inline-flex justify-content-between text-nowrap">
				<button id="cancel-mass-edit-methods-btn" type="button" class="btn btn-light" data-bs-dismiss="modal">
					{{ $t('misc.cancel') }}
				</button>
				<button id="reset-mass-edit-methods-btn" type="button" class="btn btn-light" @click="applyDefaults()">
					{{ $t('misc.reset') }}
				</button>
				<button id="save-mass-edit-methods-btn" type="button" class="btn btn-primary" @click="apply()" :disabled="!canSave">
					{{ $t('misc.save') }}
				</button>
			</div>
		</template>
	</Modal>
</template>

<style lang="sass" scoped>
@import ~bootstrap/scss/functions
@import ~bootstrap/scss/variables

.ellipsis
	overflow: hidden
	text-overflow: ellipsis

.subrules
	background-color: #f8f8f8
	padding: 1em
	margin-left: 1em
</style>
